import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  MdClose,
  MdPhotoCamera,
} from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { addReviewWithImagesData } from "../../../store/actions/reviewActions";
import { useTranslation } from "react-i18next";

function ReviewModal({ 
  product, 
  order, 
  isOpen, 
  onClose, 
  onReviewSubmitted,
  theme,
  businessUuid,
  customerProfile 
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [photoPreview, setPhotoPreview] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = React.useRef(null);
  const MAX_PHOTOS = 4; 

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = [...photoFiles];
    const newPreviews = [...photoPreview];

    // Calculate how many more photos we can add
    const availableSlots = MAX_PHOTOS - newFiles.length;
    if (availableSlots <= 0) {
      toast.error(t("STORENEWDETAILS.REVIEW_MODAL.ERRORS.MAX_PHOTOS", { max: MAX_PHOTOS }));
      return;
    }

    // Only process up to the available slots
    files.slice(0, availableSlots).forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("STORENEWDETAILS.REVIEW_MODAL.ERRORS.FILE_SIZE"));
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(t("STORENEWDETAILS.REVIEW_MODAL.ERRORS.FILE_TYPE"));
        return;
      }

      newFiles.push(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        newPreviews.push(event.target.result);
        setPhotoPreview([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });

    setPhotoFiles(newFiles);
  };

  const removePhoto = (index) => {
    const newFiles = [...photoFiles];
    const newPreviews = [...photoPreview];
    
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    
    setPhotoFiles(newFiles);
    setPhotoPreview(newPreviews);
  };

  const handleSubmitReview = async () => {
    if (!rating || !comment.trim()) {
      toast.error(t("STORENEWDETAILS.REVIEW_MODAL.ERRORS.RATING_COMMENT_REQUIRED"));
      return;
    }

    if (comment.trim().length < 10) {
      toast.error(t("STORENEWDETAILS.REVIEW_MODAL.ERRORS.COMMENT_MIN_LENGTH"));
      return;
    }

    if (!customerProfile || customerProfile.isGuest) {
      toast.error(t("STORENEWDETAILS.REVIEW_MODAL.ERRORS.LOGIN_REQUIRED"));
      return;
    }

    setSubmitting(true);
    try {
      // Create FormData to handle file uploads
      const formData = new FormData();
      formData.append('rating', rating);
      formData.append('title', title.trim());
      formData.append('comment', comment.trim());
      formData.append('customerId', customerProfile._id || customerProfile.id || customerProfile.customerId);
      formData.append('customerName', customerProfile.name || customerProfile.customerName || 'Anonymous');
      
      // Append all photo files
      photoFiles.forEach((file, index) => {
        formData.append('reviewImages', file);
      });

      // Use Redux action to submit review
      const result = await dispatch(addReviewWithImagesData(
        businessUuid, 
        product.productId || product._id || product.id, 
        formData
      ));
      
      if (!result) {
        throw new Error('Failed to add review');
      }

      // Reset form
      setRating(5);
      setTitle('');
      setComment('');
      setPhotoPreview([]);
      setPhotoFiles([]);
      
      toast.success(t("STORENEWDETAILS.REVIEW_MODAL.SUCCESS"));
      
      // Call the onReviewSubmitted callback with the product and review data
      onReviewSubmitted({
        product: product,
        review: {
          rating,
          comment: comment.trim(),
          title: title.trim(),
          photosCount: photoFiles.length
        }
      });
      
      onClose();
      
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.message || t("STORENEWDETAILS.REVIEW_MODAL.ERRORS.FAILED"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const productName = product?.productName || product?.name || 'Product';
  const productImage = product?.itemPicture || product?.image || '';

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm transition-all duration-300"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          borderRadius: theme?.borderRadius || '16px',
          fontFamily: theme?.fontFamily || 'Inter'
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 p-3 rounded-full bg-white shadow-xl hover:bg-gray-100 transition-colors hover:scale-110"
          style={{ borderRadius: '50%' }}
          title={t("STORENEWDETAILS.REVIEW_MODAL.CLOSE")}
        >
          <MdClose className="h-6 w-6 text-gray-700" />
        </button>

        <div className="p-6 overflow-y-auto max-h-[90vh]">
          {/* Header */}
          <div className="mb-6">
            <h2 
              className="text-2xl font-bold mb-2"
              style={{ color: theme?.headingColor || '#111827' }}
            >
              {t("STORENEWDETAILS.REVIEW_MODAL.TITLE")}
            </h2>
            <p className="text-gray-600">
               {t("STORENEWDETAILS.REVIEW_MODAL.SUBTITLE")}{productName }
            </p>
          </div>

          {/* Product Info */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            {productImage && (
              <img
                src={productImage.includes('http') ? productImage : `https://dukanbuddy-normal.s3.us-east-1.amazonaws.com/${productImage}`}
                alt={productName}
                className="w-16 h-16 object-cover rounded-lg"
              />
            )}
            <div>
              <h3 
                className="font-semibold"
                style={{ color: theme?.headingColor || '#111827' }}
              >
                {productName}
              </h3>
              <p className="text-sm text-gray-600">
                From Order #{order?.orderId || 'Unknown'}
              </p>
              <div className="flex items-center gap-2 mt-1 text-sm text-green-600">
                <FaCheckCircle />
                <span>Delivered on {new Date(order?.updatedAt || Date.now()).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Rating */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3">
              {t("STORENEWDETAILS.REVIEW_MODAL.RATING_LABEL")}
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`text-3xl ${star <= rating ? 'text-yellow-500' : 'text-gray-300'} hover:scale-110 transition-transform`}
                >
                  ★
                </button>
              ))}
            </div>
            <div className="flex justify-between text-sm text-gray-500 mt-2">
              <span>{t("STORENEWDETAILS.REVIEW_MODAL.POOR")}</span>
              <span>{t("STORENEWDETAILS.REVIEW_MODAL.EXCELLENT")}</span>
            </div>
          </div>

          {/* Review Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              {t("STORENEWDETAILS.REVIEW_MODAL.REVIEW_TITLE")}
            </label>
            <input
              type="text"
              placeholder={t("STORENEWDETAILS.REVIEW_MODAL.TITLE_PLACEHOLDER")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-3 border rounded-lg text-sm"
              style={{
                fontFamily: theme?.fontFamily,
                color: theme?.textColor || '#1f2937',
                borderColor: '#e5e7eb',
                borderRadius: theme?.borderRadius || '8px'
              }}
            />
          </div>

          {/* Review Comment */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              {t("STORENEWDETAILS.REVIEW_MODAL.REVIEW_COMMENT")}
            </label>
            <textarea
              placeholder={t("STORENEWDETAILS.REVIEW_MODAL.COMMENT_PLACEHOLDER")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
              className="w-full p-3 border rounded-lg text-sm resize-none"
              style={{
                fontFamily: theme?.fontFamily,
                color: theme?.textColor || '#1f2937',
                borderColor: '#e5e7eb',
                borderRadius: theme?.borderRadius || '8px'
              }}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("STORENEWDETAILS.REVIEW_MODAL.MIN_CHARACTERS")}
            </p>
          </div>

         {/* Photos Section - Redesigned */}
<div className="mb-8">
  <div className="flex items-center justify-between mb-3">
    <label className="block text-sm font-medium text-gray-700">
      {t("STORENEWDETAILS.REVIEW_MODAL.PHOTOS_LABEL")} <span className="text-gray-400 font-normal">{t("STORENEWDETAILS.REVIEW_MODAL.PHOTOS_OPTIONAL")}</span>
    </label>
    <span className="text-xs font-medium px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
      {photoPreview.length}/{MAX_PHOTOS} photos
    </span>
  </div>

  {/* Photo Grid */}
  <div className="grid grid-cols-5 gap-3 mb-2">
    {/* Existing Photos */}
    {photoPreview.map((preview, index) => (
      <div 
        key={index} 
        className="relative group aspect-square"
      >
        <div className="relative w-full h-full rounded-xl overflow-hidden border-2 border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
          <img
            src={preview}
            alt={`${t("STORENEWDETAILS.REVIEW_MODAL.REVIEW_PHOTO")} ${index + 1}`}
            className="w-full h-full object-cover"
          />
          {/* Overlay with photo number */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          
          {/* Photo badge */}
          <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full backdrop-blur-sm">
            #{index + 1}
          </div>
        </div>
        
        {/* Remove button */}
        <button
          type="button"
          onClick={() => removePhoto(index)}
          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-600 hover:scale-110 z-10"
          title={t("STORENEWDETAILS.REVIEW_MODAL.REMOVE_PHOTO")}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    ))}

    {/* Add Photo Button - Only show if less than MAX_PHOTOS */}
    {photoPreview.length < MAX_PHOTOS && (
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-200 group"
        style={{
          borderRadius: theme?.borderRadius || '12px',
          borderColor: theme?.buttonColor ? `${theme.buttonColor}40` : '#e5e7eb'
        }}
      >
        <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-indigo-100 flex items-center justify-center transition-colors duration-200">
          <MdPhotoCamera 
            size={24} 
            className="text-gray-400 group-hover:text-indigo-500 transition-colors duration-200" 
          />
        </div>
        <span className="text-xs font-medium text-gray-500 group-hover:text-indigo-600 transition-colors duration-200">
          {t("STORENEWDETAILS.REVIEW_MODAL.ADD_PHOTO")}
        </span>
        <span className="text-[10px] text-gray-400">
          {MAX_PHOTOS - photoPreview.length} left
        </span>
      </button>
    )}
  </div>

  {/* Hidden file input */}
  <input
    ref={fileInputRef}
    type="file"
    accept="image/*"
    multiple
    onChange={handlePhotoUpload}
    className="hidden"
  />

  {/* Photo upload info with better styling */}
  <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
    <div className="flex items-start gap-2">
      <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div className="flex-1">
        <p className="text-xs text-gray-600">
          <span className="font-medium">{t("STORENEWDETAILS.REVIEW_MODAL.REQUIREMENTS")}</span> {t("STORENEWDETAILS.REVIEW_MODAL.REQUIREMENTS_TEXT")}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {t("STORENEWDETAILS.REVIEW_MODAL.REQUIREMENTS_HELP")}
        </p>
      </div>
    </div>
    
    {/* Upload progress indicator */}
    {photoPreview.length > 0 && (
      <div className="mt-3 flex items-center gap-2 text-xs">
        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-green-500 rounded-full transition-all duration-300"
            style={{ width: `${(photoPreview.length / MAX_PHOTOS) * 100}%` }}
          />
        </div>
        <span className="text-green-600 font-medium flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {t("STORENEWDETAILS.REVIEW_MODAL.PHOTOS_READY", { count: photoPreview.length })}
        </span>
      </div>
    )}
  </div>

  {/* Success message for uploaded photos */}
  {photoPreview.length === MAX_PHOTOS && (
    <div className="mt-3 p-2 bg-green-50 text-green-700 text-xs rounded-lg flex items-center gap-2 border border-green-200">
      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      <span>{t("STORENEWDETAILS.REVIEW_MODAL.MAX_PHOTOS_REACHED", { max: MAX_PHOTOS })}</span>
    </div>
  )}
</div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-6 font-medium border rounded-lg hover:bg-gray-50 transition-colors"
              style={{
                borderColor: '#e5e7eb',
                color: theme?.textColor || '#1f2937',
                borderRadius: theme?.borderRadius || '8px'
              }}
            >
              {t("STORENEWDETAILS.REVIEW_MODAL.CANCEL")}
            </button>
            <button
              onClick={handleSubmitReview}
              disabled={submitting || !comment.trim() || comment.trim().length < 10}
              className="flex-1 py-3 px-6 font-medium text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme?.buttonColor || '#4f46e5',
                borderRadius: theme?.borderRadius || '8px'
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t("STORENEWDETAILS.REVIEW_MODAL.SUBMITTING")}
                </span>
              ) : (
                photoPreview.length > 0 
                  ? t("STORENEWDETAILS.REVIEW_MODAL.SUBMIT_WITH_PHOTOS", { count: photoPreview.length })
                  : t("STORENEWDETAILS.REVIEW_MODAL.SUBMIT")
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReviewModal;